import { createSelector } from '@reduxjs/toolkit';

import { RootState } from 'modules/main/redux';
import { ServerRoute, DownloadedRoute } from '../types';

export const serverRoutingsListSelector = (state: RootState): ServerRoute[] =>
  state.routing.serverRoutings.list;

export const hasNewServerRoutingForRefreshSelector = (
  state: RootState,
): boolean => state.routing.serverRoutings.hasNew;

export const downloadedRoutesSelector = (
  state: RootState,
): { [key: string]: DownloadedRoute } => state.routing.downloadedRoutings;

export const activeIsochronesStorageKeySelector = (state: RootState): string =>
  state.routing.activeIsochronesStorageKey;

export const sideMenuRoutingIdsSelector = (state: RootState): string[] =>
  state.routing.sideMenuRoutingIds.list.filter(Boolean) as string[];

export const sideMenuRoutingIdsWithIndexSelector = createSelector(
  [
    (state: RootState): (string | null)[] =>
      state.routing.sideMenuRoutingIds.list,
  ],
  (list): { [key: string]: number } =>
    list.reduce((acc, id, index) => {
      if (id) {
        acc[id] = index;
      }
      return acc;
    }, {} as { [key: string]: number }),
);

export const activeRoutingsIdsSelector = (state: RootState): string[] =>
  state.routing.activeRoutingsIds;

export const downloadedRoutingByIdSelector = (
  state: RootState,
  id: string,
): DownloadedRoute | undefined => state.routing.downloadedRoutings[id];

export const serverRoutingsListLastRequestTimestampSelector = (
  state: RootState,
): number => state.routing.serverRoutings.lastTimestamp;

export const downloadedRoutingsListSelector = (
  state: RootState,
): DownloadedRoute[] =>
  Object.values(state.routing.downloadedRoutings).filter(
    (r) => r.id !== undefined,
  );

export const sortedDownloadedRoutingsListSelector = createSelector(
  [downloadedRoutingsListSelector],
  (routings) => {
    const sortedRoutings = [...routings];
    sortedRoutings.sort((a, b) =>
      a.metadata?.Statistics?.startTime < b.metadata?.Statistics?.startTime
        ? 1
        : -1,
    );
    return sortedRoutings;
  },
);

export const sortedSideMenuRoutingIdsSelector = createSelector(
  [sortedDownloadedRoutingsListSelector, sideMenuRoutingIdsSelector],
  (routings, ids) =>
    routings.filter((r) => ids.includes(r.id)).map((r) => r.id),
);

export const sideMenuRoutingsListSelector = createSelector(
  [downloadedRoutingsListSelector, sideMenuRoutingIdsSelector],
  (routings, ids) => {
    return routings.filter((r) => ids.includes(r.id));
  },
);

const getServerRoutingTime = (route: ServerRoute): string =>
  route.v === 2 ? route.created_at : route.requestTime;

export const sortedServerRoutingsListSelector = createSelector(
  [serverRoutingsListSelector, downloadedRoutingsListSelector],
  (serverRoutings, downloadedRoutings) => {
    const downloadedRoutesIds = downloadedRoutings.map((d) => d.id);
    const sorted = [...serverRoutings];
    sorted.sort((a, b) =>
      getServerRoutingTime(a) < getServerRoutingTime(b) ? 1 : -1,
    );
    return sorted.filter((r) => !downloadedRoutesIds.includes(String(r.id)));
  },
);
